import { Container } from '@mui/material';
import { useEffect } from 'react';
import ReactGA from 'react-ga';

const Privacy = () => {
  useEffect(() => {
    ReactGA.pageview('privacy');
  }, []);

  return (
    <Container maxWidth="md">
      <p>SUMMARY OF KEY POINTS</p>

      <p>
        This summary provides key points from our privacy notice, but you can
        find out more details about any of these topics by clicking the link
        following each key point or by using our&Acirc; table of contents&Acirc;
        below to find the section you are looking for.
      </p>

      <p>
        What personal information do we process? When you visit, use, or
        navigate our Services, we may process personal information depending on
        how you interact with Individual and the Services, the choices you make,
        and the products and features you use. Learn more about&Acirc; personal
        information you disclose to us.
      </p>

      <p>
        Do we process any sensitive personal information? We do not process
        sensitive personal information.
      </p>

      <p>
        Do we receive any information from third parties? We do not receive any
        information from third parties.
      </p>

      <p>
        How do we process your information? We process your information to
        provide, improve, and administer our Services, communicate with you, for
        security and fraud prevention, and to comply with law. We may also
        process your information for other purposes with your consent. We
        process your information only when we have a valid legal reason to do
        so. Learn more about&Acirc; how we process your information.
      </p>

      <p>
        In what situations and with which parties do we share personal
        information? We may share information in specific situations and with
        specific third parties. Learn more about&Acirc; when and with whom we
        share your personal information.
      </p>

      <p>
        How do we keep your information safe? We have organizational and
        technical processes and procedures in place to protect your personal
        information. However, no electronic transmission over the internet or
        information storage technology can be guaranteed to be 100% secure, so
        we cannot promise or guarantee that hackers, cybercriminals, or other
        unauthorized third parties will not be able to defeat our security and
        improperly collect, access, steal, or modify your information. Learn
        more about&Acirc; how we keep your information safe.
      </p>

      <p>
        What are your rights? Depending on where you are located geographically,
        the applicable privacy law may mean you have certain rights regarding
        your personal information. Learn more about&Acirc; your privacy rights.
      </p>

      <p>
        How do you exercise your rights? The easiest way to exercise your rights
        is by submitting a&Acirc; data subject access request, or by contacting
        us. We will consider and act upon any request in accordance with
        applicable data protection laws.
      </p>

      <p>
        Want to learn more about what Individual does with any information we
        collect?&Acirc; Review the privacy notice in full.
      </p>

      <p> TABLE OF CONTENTS</p>

      <p>
        1. WHAT INFORMATION DO WE COLLECT? 2. HOW DO WE PROCESS YOUR
        INFORMATION? 3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL
        INFORMATION? 4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL
        INFORMATION?&Acirc; &Acirc; &Acirc; 5. DO WE USE COOKIES AND OTHER
        TRACKING TECHNOLOGIES? 6. HOW LONG DO WE KEEP YOUR INFORMATION? 7. HOW
        DO WE KEEP YOUR INFORMATION SAFE? 8. DO WE COLLECT INFORMATION FROM
        MINORS? 9. WHAT ARE YOUR PRIVACY RIGHTS? 10. CONTROLS FOR DO-NOT-TRACK
        FEATURES 11. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS? 12.
        DO WE MAKE UPDATES TO THIS NOTICE? 13. HOW CAN YOU CONTACT US ABOUT THIS
        NOTICE? 14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT
        FROM YOU?
      </p>

      <p> 1. WHAT INFORMATION DO WE COLLECT?</p>

      <p>Personal information you disclose to us</p>

      <p>
        In Short:&Acirc; We collect personal information that you provide to us.
      </p>

      <p>
        We collect personal information that you voluntarily provide to us when
        you express an interest in obtaining information about us or our
        products and Services, when you participate in activities on the
        Services, or otherwise when you contact us.
      </p>

      <p>Sensitive Information. We do not process sensitive information.</p>

      <p>
        All personal information that you provide to us must be true, complete,
        and accurate, and you must notify us of any changes to such personal
        information.
      </p>

      <p>Information automatically collected</p>

      <p>
        In Short:&Acirc; Some information &acirc;&euro;&rdquo; such as your
        Internet Protocol (IP) address and/or browser and device characteristics
        &acirc;&euro;&rdquo; is collected automatically when you visit our
        Services.
      </p>

      <p>
        We automatically collect certain information when you visit, use, or
        navigate the Services. This information does not reveal your specific
        identity (like your name or contact information) but may include device
        and usage information, such as your IP address, browser and device
        characteristics, operating system, language preferences, referring URLs,
        device name, country, location, information about how and when you use
        our Services, and other technical information. This information is
        primarily needed to maintain the security and operation of our Services,
        and for our internal analytics and reporting purposes.
      </p>

      <p>
        Like many businesses, we also collect information through cookies and
        similar technologies.
      </p>

      <p>
        The information we collect includes: Log and Usage Data. Log and usage
        data is service-related, diagnostic, usage, and performance information
        our servers automatically collect when you access or use our Services
        and which we record in log files. Depending on how you interact with us,
        this log data may include your IP address, device information, browser
        type, and settings and information about your activity in the
        Services&Acirc; (such as the date/time stamps associated with your
        usage, pages and files viewed, searches, and other actions you take such
        as which features you use), device event information (such as system
        activity, error reports (sometimes called &quot;crash dumps&quot;), and
        hardware settings). Device Data. We collect device data such as
        information about your computer, phone, tablet, or other device you use
        to access the Services. Depending on the device used, this device data
        may include information such as your IP address (or proxy server),
        device and application identification numbers, location, browser type,
        hardware model, Internet service provider and/or mobile carrier,
        operating system, and system configuration information. Location Data.
        We collect location data such as information about your device's
        location, which can be either precise or imprecise. How much information
        we collect depends on the type and settings of the device you use to
        access the Services. For example, we may use GPS and other technologies
        to collect geolocation data that tells us your current location (based
        on your IP address). You can opt out of allowing us to collect this
        information either by refusing access to the information or by disabling
        your Location setting on your device. However, if you choose to opt out,
        you may not be able to use certain aspects of the Services. 2. HOW DO WE
        PROCESS YOUR INFORMATION?
      </p>

      <p>
        In Short:&Acirc; We process your information to provide, improve, and
        administer our Services, communicate with you, for security and fraud
        prevention, and to comply with law. We may also process your information
        for other purposes with your consent.
      </p>

      <p>
        We process your personal information for a variety of reasons, depending
        on how you interact with our Services, including:
      </p>

      <p>
        {' '}
        To save or protect an individual's vital interest. We may process your
        information when necessary to save or protect an
        individual&acirc;&euro;&trade;s vital interest, such as to prevent harm.
      </p>

      <p>3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?</p>

      <p>
        In Short:&Acirc; We only process your personal information when we
        believe it is necessary and we have a valid legal reason (i.e., legal
        basis) to do so under applicable law, like with your consent, to comply
        with laws, to provide you with services to enter into or fulfill our
        contractual obligations, to protect your rights, or to fulfill our
        legitimate business interests.
      </p>

      <p>
        The General Data Protection Regulation (GDPR) and UK GDPR require us to
        explain the valid legal bases we rely on in order to process your
        personal information. As such, we may rely on the following legal bases
        to process your personal information: Consent.&Acirc; We may process
        your information if you have given us permission (i.e., consent) to use
        your personal information for a specific purpose. You can withdraw your
        consent at any time. Learn more about&Acirc; withdrawing your consent.
        Legal Obligations. We may process your information where we believe it
        is necessary for compliance with our legal obligations, such as to
        cooperate with a law enforcement body or regulatory agency, exercise or
        defend our legal rights, or disclose your information as evidence in
        litigation in which we are involved. Vital Interests. We may process
        your information where we believe it is necessary to protect your vital
        interests or the vital interests of a third party, such as situations
        involving potential threats to the safety of any person.
      </p>

      <p>4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</p>

      <p>
        In Short:&Acirc; We may share information in specific situations
        described in this section and/or with the following third parties.
      </p>

      <p>
        We may need to share your personal information in the following
        situations: Business Transfers. We may share or transfer your
        information in connection with, or during negotiations of, any merger,
        sale of company assets, financing, or acquisition of all or a portion of
        our business to another company.
      </p>

      <p>5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</p>

      <p>
        In Short:&Acirc; We may use cookies and other tracking technologies to
        collect and store your information.
      </p>

      <p>
        We may use cookies and similar tracking technologies (like web beacons
        and pixels) to access or store information. Specific information about
        how we use such technologies and how you can refuse certain cookies is
        set out in our Cookie Notice.
      </p>

      <p>6. HOW LONG DO WE KEEP YOUR INFORMATION?</p>

      <p>
        In Short:&Acirc; We keep your information for as long as necessary to
        fulfill the purposes outlined in this privacy notice unless otherwise
        required by law.
      </p>

      <p>
        We will only keep your personal information for as long as it is
        necessary for the purposes set out in this privacy notice, unless a
        longer retention period is required or permitted by law (such as tax,
        accounting, or other legal requirements).
      </p>

      <p>
        When we have no ongoing legitimate business need to process your
        personal information, we will either delete or anonymize such
        information, or, if this is not possible (for example, because your
        personal information has been stored in backup archives), then we will
        securely store your personal information and isolate it from any further
        processing until deletion is possible.
      </p>

      <p>7. HOW DO WE KEEP YOUR INFORMATION SAFE?</p>

      <p>
        In Short:&Acirc; We aim to protect your personal information through a
        system of organizational and technical security measures.
      </p>

      <p>
        We have implemented appropriate and reasonable technical and
        organizational security measures designed to protect the security of any
        personal information we process. However, despite our safeguards and
        efforts to secure your information, no electronic transmission over the
        Internet or information storage technology can be guaranteed to be 100%
        secure, so we cannot promise or guarantee that hackers, cybercriminals,
        or other unauthorized third parties will not be able to defeat our
        security and improperly collect, access, steal, or modify your
        information. Although we will do our best to protect your personal
        information, transmission of personal information to and from our
        Services is at your own risk. You should only access the Services within
        a secure environment.
      </p>

      <p>8. DO WE COLLECT INFORMATION FROM MINORS?</p>

      <p>
        In Short:&Acirc; We do not knowingly collect data from or market to
        children under 18 years of age.
      </p>

      <p>
        We do not knowingly solicit data from or market to children under 18
        years of age. By using the Services, you represent that you are at least
        18 or that you are the parent or guardian of such a minor and consent to
        such minor dependent&acirc;&euro;&trade;s use of the Services. If we
        learn that personal information from users less than 18 years of age has
        been collected, we will deactivate the account and take reasonable
        measures to promptly delete such data from our records. If you become
        aware of any data we may have collected from children under age 18,
        please contact us at thanveerahamed.developer@gmail.com.
      </p>

      <p>9. WHAT ARE YOUR PRIVACY RIGHTS?</p>

      <p>
        In Short:&Acirc; In some regions, such as the European Economic Area
        (EEA) and United Kingdom (UK), you have rights that allow you greater
        access to and control over your personal information.&Acirc; You may
        review, change, or terminate your account at any time.
      </p>

      <p>
        In some regions (like the EEA and UK), you have certain rights under
        applicable data protection laws. These may include the right (i) to
        request access and obtain a copy of your personal information, (ii) to
        request rectification or erasure; (iii) to restrict the processing of
        your personal information; and (iv) if applicable, to data portability.
        In certain circumstances, you may also have the right to object to the
        processing of your personal information. You can make such a request by
        contacting us by using the contact details provided in the section
        &quot;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&quot; below.
      </p>

      <p>
        We will consider and act upon any request in accordance with applicable
        data protection laws. &Acirc; If you are located in the EEA or UK and
        you believe we are unlawfully processing your personal information, you
        also have the right to complain to your Member State data protection
        authority or&Acirc; UK data protection authority.
      </p>

      <p>
        If you are located in Switzerland, you may contact the Federal Data
        Protection and Information Commissioner.
      </p>

      <p>
        Withdrawing your consent: If we are relying on your consent to process
        your personal information, you have the right to withdraw your consent
        at any time. You can withdraw your consent at any time by contacting us
        by using the contact details provided in the section &quot;HOW CAN YOU
        CONTACT US ABOUT THIS NOTICE?&quot; below.
      </p>

      <p>
        However, please note that this will not affect the lawfulness of the
        processing before its withdrawal nor, will it affect the processing of
        your personal information conducted in reliance on lawful processing
        grounds other than consent.
      </p>

      <p>
        Cookies and similar technologies: Most Web browsers are set to accept
        cookies by default. If you prefer, you can usually choose to set your
        browser to remove cookies and to reject cookies. If you choose to remove
        cookies or reject cookies, this could affect certain features or
        services of our Services. You may also opt out of interest-based
        advertising by advertisers on our Services.
      </p>

      <p>
        If you have questions or comments about your privacy rights, you may
        email us at thanveerahamed.developer@gmail.com.
      </p>

      <p>10. CONTROLS FOR DO-NOT-TRACK FEATURES</p>

      <p>
        Most web browsers and some mobile operating systems and mobile
        applications include a Do-Not-Track (&quot;DNT&quot;) feature or setting
        you can activate to signal your privacy preference not to have data
        about your online browsing activities monitored and collected. At this
        stage no uniform technology standard for recognizing and implementing
        DNT signals has been finalized. As such, we do not currently respond to
        DNT browser signals or any other mechanism that automatically
        communicates your choice not to be tracked online. If a standard for
        online tracking is adopted that we must follow in the future, we will
        inform you about that practice in a revised version of this privacy
        notice.
      </p>

      <p>11. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</p>

      <p>
        In Short:&Acirc; Yes, if you are a resident of California, you are
        granted specific rights regarding access to your personal information.
      </p>

      <p>
        California Civil Code Section 1798.83, also known as the &quot;Shine The
        Light&quot; law, permits our users who are California residents to
        request and obtain from us, once a year and free of charge, information
        about categories of personal information (if any) we disclosed to third
        parties for direct marketing purposes and the names and addresses of all
        third parties with which we shared personal information in the
        immediately preceding calendar year. If you are a California resident
        and would like to make such a request, please submit your request in
        writing to us using the contact information provided below.
      </p>

      <p>
        If you are under 18 years of age, reside in California, and have a
        registered account with Services, you have the right to request removal
        of unwanted data that you publicly post on the Services. To request
        removal of such data, please contact us using the contact information
        provided below and include the email address associated with your
        account and a statement that you reside in California. We will make sure
        the data is not publicly displayed on the Services, but please be aware
        that the data may not be completely or comprehensively removed from all
        our systems (e.g., backups, etc.).
      </p>

      <p>12. DO WE MAKE UPDATES TO THIS NOTICE?</p>

      <p>
        In Short:&Acirc; Yes, we will update this notice as necessary to stay
        compliant with relevant laws.
      </p>

      <p>
        We may update this privacy notice from time to time. The updated version
        will be indicated by an updated &quot;Revised&quot; date and the updated
        version will be effective as soon as it is accessible. If we make
        material changes to this privacy notice, we may notify you either by
        prominently posting a notice of such changes or by directly sending you
        a notification. We encourage you to review this privacy notice
        frequently to be informed of how we are protecting your information.
      </p>

      <p>13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</p>

      <p>
        If you have questions or comments about this notice, you may email us at
        thanveerahamed.developer@gmail.com&Acirc; or by post to:
      </p>

      <p>Individual __________ Amsterdam Netherlands</p>

      <p>
        14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
      </p>

      <p>
        You have the right to request access to the personal information we
        collect from you, change that information, or delete it. To request to
        review, update, or delete your personal information, please fill out and
        submit a&Acirc; data subject access request. This privacy policy was
        created using Termly's Privacy Policy Generator.
      </p>
    </Container>
  );
};

export default Privacy;
